/**
 * DashboardDesktop — десктоп-раскладка дашборда (этап метрик): sticky-шапка
 * (поиск-стаб, колокол, «Создать аккаунт»), секция «Ключевые метрики» с
 * сегментом периода и сеткой из 6 карточек. Графики «Трендов» — следующим
 * этапом (Recharts). Данные/период держит оркестратор Dashboard.
 */

import {Bell, Plus, Search} from 'lucide-react';

import type {SubscriptionStatus} from '@services/clients';
import type {DashboardSummary} from '@services/dashboard';
import {Button} from '@components/ui/Button';
import {Loader} from '@components/ui/Loader';

import {type DashPeriod} from '../Dashboard.utils';
import {PeriodSegment} from '../PeriodSegment';
import {TrendsSection} from '../TrendsSection';

import {MetricsGrid} from './MetricsGrid';

type DashboardDesktopProps = {
  summary?: DashboardSummary;
  isLoading: boolean;
  period: DashPeriod;
  onPeriod: (p: DashPeriod) => void;
  onPickRisk: (status: SubscriptionStatus) => void;
  onCreate: () => void;
  onSoon: (text: string) => void;
};

export const DashboardDesktop = ({
  summary,
  isLoading,
  period,
  onPeriod,
  onPickRisk,
  onCreate,
  onSoon,
}: DashboardDesktopProps) => (
  <>
    <header className="sticky top-0 z-10 flex items-center gap-4 border-b border-skr-line bg-background/80 px-[30px] py-[18px] backdrop-blur">
      <div className="min-w-0 flex-1">
        <h1 className="text-[21px] font-bold -tracking-[.02em]">Дашборд</h1>
        <div className="mt-0.5 text-[13px] text-muted-foreground">
          Обзор платформы · {summary?.dateLabel ?? '—'}
        </div>
      </div>
      <button
        onClick={() => onSoon('Поиск — скоро')}
        className="flex h-10 w-[262px] items-center gap-2.5 rounded-[10px] border border-input bg-card px-3 text-[13.5px] text-skr-faint"
      >
        <Search className="size-4" strokeWidth={1.9} />
        Поиск клиента, телефона…
      </button>
      <Button
        variant="outline"
        size="icon"
        aria-label="Уведомления"
        onClick={() => onSoon('Уведомления — скоро')}
      >
        <Bell />
      </Button>
      <Button onClick={onCreate}>
        <Plus />
        Создать аккаунт
      </Button>
    </header>

    <div className="flex flex-col gap-6 px-[30px] pb-10 pt-[26px]">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-[15px] font-semibold text-skr-ink">Ключевые метрики</h2>
        <div className="w-[320px]">
          <PeriodSegment period={period} onChange={onPeriod} />
        </div>
      </div>

      {isLoading || !summary ? (
        <div className="flex min-h-[360px] items-center justify-center">
          <Loader label="Загружаем метрики…" />
        </div>
      ) : (
        <MetricsGrid s={summary} onPickRisk={onPickRisk} />
      )}

      <TrendsSection />
    </div>
  </>
);
